import React from "react";
import { graphql } from "react-relay";
import { type RouteComponentProps, withRouter } from "react-router-dom";

import {
	faBan,
	faCopy,
	faFileCsv,
	faHandPointRight,
	faPrint,
	faShare,
	faUserEdit,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/light";

import { BillingIdentity } from "~/billing";
import { confirm, warning } from "~/lib/prompts";
import { commitMutationPromise, smart } from "~/lib/relayUtil";
import { simulateClick } from "~/lib/utils";
import { environment } from "~/models";
import { DropdownItem } from "~/ui/ArrowDropdown";
import toast from "~/ui/toast";

import type { FeedbackItemQuery$data as QueryResponse } from "~/__generated__/FeedbackItemQuery.graphql";
import type { MoreActions_item$data as Feedback } from "~/__generated__/MoreActions_item.graphql";
import type { MoreActionsAddRecipientMutation as AddRecipientType } from "~/__generated__/MoreActionsAddRecipientMutation.graphql";
import type { MoreActionsCancelFurtherResponsesMutation as CancelResponsesType } from "~/__generated__/MoreActionsCancelFurtherResponsesMutation.graphql";

import styles from "./css/MoreActions.module.css";

type Viewer = QueryResponse["viewer"];

type Props = RouteComponentProps & {
	feedback: Feedback;
	viewer: Viewer;
	nudgeUsers: (e: React.MouseEvent, selected?: string) => void;
	onExportCSV?: () => void;
	onReuseQuestions: () => void;
	showReuseQuestions: boolean;
};

const CancelFurtherResponsesMutation = graphql`
	mutation MoreActionsCancelFurtherResponsesMutation($id: ID!) {
		cancelFurtherResponses(id: $id) {
			feedback {
				id

				requestCount
				responseCount

				requests {
					id
					feedbackRecord {
						id
					}
				}
			}
		}
	}
`;

const AddRecipientMutation = graphql`
	mutation MoreActionsAddRecipientMutation($id: ID!, $userId: ID!) {
		addFeedbackRecipient(id: $id, userId: $userId) {
			recipient {
				id

				feedback {
					...MoreActions_item
				}
			}
		}
	}
`;

export const shareFeedbackWith = async (
	feedbackId: string,
	user: { id: string; fullName: string; firstName?: string | null },
) => {
	const result = await confirm(
		<p className={styles.shareFeedbackWithBody}>
			{user.firstName ?? user.fullName} will be able to see all responses, including who sent them.
		</p>,
		{
			yesText: "Share",
			title: "Share feedback?",
		},
	);

	if (typeof result !== "boolean" || !result) {
		return;
	}

	try {
		const { addFeedbackRecipient } = await commitMutationPromise<AddRecipientType>(environment, {
			mutation: AddRecipientMutation,
			variables: { id: feedbackId, userId: user.id },
		});

		if (addFeedbackRecipient) {
			toast.success(`Feedback shared successfully`);
		} else {
			toast.error(`Could not share feedback`);
		}
	} catch (errors) {
		if (Array.isArray(errors)) {
			toast.error(`Could not share feedback: ${errors[0].message}`);
		}
	}
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function callAndClose(cb: (...args: any) => void) {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	return function (...args: any) {
		cb(...args);
		simulateClick(document.body);
	};
}
class MoreActions extends React.PureComponent<Props> {
	static Fragments = {
		feedback: graphql`
			fragment MoreActions_item on FeedbackNode {
				id
				type
				isDraft
				sharedWithMe
				isThreeSixtyParent

				creator {
					id
				}

				context {
					__typename
					... on User {
						id
						fullName
						firstName
					}
				}

				requests {
					id
					feedbackRecord {
						id
					}
				}

				recipients {
					user {
						id
					}
				}
			}
		`,
	};

	static defaultProps = {
		showReuseQuestions: true,
	};

	cancelFurtherResponses = async () => {
		const {
			feedback: { id },
		} = this.props;

		const res = await warning("Cancelling further responses is a permanent action.", {
			title: "Are you sure?",
			yesProps: { variant: "destructive" },
			noProps: { variant: "secondary" },
			yesText: "Yes",
			noText: "No",
		});

		if (!res) return;

		try {
			const { cancelFurtherResponses } = await commitMutationPromise<CancelResponsesType>(environment, {
				mutation: CancelFurtherResponsesMutation,
				variables: { id },
			});

			if (cancelFurtherResponses) {
				toast.success(`Further responses canceled`);
			} else {
				toast.error(`Could not cancel further responses`);
			}
		} catch (errors) {
			if (Array.isArray(errors)) {
				toast.error(`Could not cancel further responses: ${errors[0]?.message}`);
			}
		}
	};

	addRecipients = () => {
		const { feedback, history } = this.props;
		if (!feedback || feedback.isDraft) return;

		if (feedback.isThreeSixtyParent) {
			return history.push(`/feedback/three-sixty/${feedback.id}/edit/`);
		}

		return history.push(`/feedback/${feedback.id}/edit/`, { field: "ask" });
	};

	shareWithOthers = () => {
		const { feedback, history } = this.props;
		if (!feedback || feedback.isDraft) return;

		if (feedback.isThreeSixtyParent) {
			return history.push(`/feedback/three-sixty/${feedback.id}/edit/`);
		}

		return history.push(`/feedback/${feedback.id}/edit/`, { field: "share" });
	};

	shareWithManager = () => {
		const {
			feedback: { id },
			viewer: { user },
		} = this.props;

		const manager = user.internalAttributes?.manager;

		if (!manager) return;
		shareFeedbackWith(id, manager);
	};

	shareWithDirectReport = () => {
		const {
			feedback: { id, context },
		} = this.props;

		if (!("id" in context[0])) return;

		shareFeedbackWith(id, context[0]);
	};

	print = () => {
		setTimeout(window.print, 0);
	};
	renderCreatorActions = (sharableWithManager: boolean, sharableWithDirectReport: boolean) => {
		const { feedback, onReuseQuestions, viewer, showReuseQuestions, onExportCSV } = this.props;
		const sharable = sharableWithManager || sharableWithDirectReport;

		const completeRequests = feedback.requests.filter(({ feedbackRecord }) => feedbackRecord);
		const allResponsesCollected = feedback.requests.length === completeRequests.length;

		let directReportName = "Direct Report";
		const context = this.props.feedback.context[0];
		if (
			context.__typename === "User" &&
			viewer.user.internalAttributes?.directReports?.edges.some(edge => edge?.node && edge.node.id === context.id)
		) {
			directReportName = context.fullName;
		}

		let managerName = viewer.user.internalAttributes?.manager?.fullName ?? "Manager";

		return (
			<>
				{!allResponsesCollected && (
					<>
						<DropdownItem icon={<DropdownItem.Icon icon={faBan} />} onClick={callAndClose(this.cancelFurtherResponses)}>
							Cancel further responses
						</DropdownItem>
						<DropdownItem
							icon={<DropdownItem.Icon icon={faHandPointRight} />}
							onClick={callAndClose(this.props.nudgeUsers)}
						>
							Remind respondents…
						</DropdownItem>
					</>
				)}

				<DropdownItem icon={<DropdownItem.Icon icon={faUserEdit} />} onClick={callAndClose(this.addRecipients)}>
					Edit recipients…
				</DropdownItem>
				<DropdownItem icon={<DropdownItem.Icon icon={faShare} />} onClick={callAndClose(this.shareWithOthers)}>
					Share with…
				</DropdownItem>
				{sharable && (
					<DropdownItem
						icon={<DropdownItem.Icon icon={faShare} />}
						onClick={callAndClose(sharableWithManager ? this.shareWithManager : this.shareWithDirectReport)}
					>
						{`Share with ${sharableWithManager ? managerName : directReportName}…`}
					</DropdownItem>
				)}

				{showReuseQuestions ? (
					<>
						<hr className={styles.dropdownDivider} />
						<BillingIdentity feature="feedback" deprecated viewer={viewer}>
							{(extraProps, icon) => (
								<DropdownItem
									icon={<DropdownItem.Icon icon={faCopy} />}
									onClick={callAndClose(onReuseQuestions)}
									{...extraProps}
								>
									Re-use questions… &ensp;{icon}
								</DropdownItem>
							)}
						</BillingIdentity>
					</>
				) : null}

				<hr className={styles.dropdownDivider} />
				<DropdownItem icon={<DropdownItem.Icon icon={faPrint} />} onClick={callAndClose(this.print)}>
					Print...
				</DropdownItem>
				{onExportCSV && (
					<DropdownItem icon={<DropdownItem.Icon icon={faFileCsv} />} onClick={onExportCSV}>
						Export CSV…
					</DropdownItem>
				)}
			</>
		);
	};

	renderSharedWithMe = () => {
		const { onReuseQuestions, viewer, showReuseQuestions, onExportCSV } = this.props;

		if (!showReuseQuestions && !onExportCSV) {
			return null;
		}

		return (
			<>
				{showReuseQuestions && (
					<BillingIdentity feature="feedback" deprecated viewer={viewer}>
						{(extraProps, icon) => (
							<>
								<DropdownItem icon={<DropdownItem.Icon icon={faCopy} />} onClick={onReuseQuestions} {...extraProps}>
									Re-use questions… &ensp;{icon}
								</DropdownItem>
							</>
						)}
					</BillingIdentity>
				)}
				{onExportCSV && (
					<DropdownItem icon={<DropdownItem.Icon icon={faFileCsv} />} onClick={onExportCSV}>
						Export CSV…
					</DropdownItem>
				)}
			</>
		);
	};

	render() {
		const {
			viewer: { user },
			feedback: { creator, sharedWithMe, context, recipients },
		} = this.props;

		const { manager, directReports } = user.internalAttributes ?? {};

		if (!creator) return null;
		const context0 = context[0];

		const isAboutMe = context0 && context0.__typename == "User" && context0.id === user.id;
		const sharableWithManager =
			!sharedWithMe && isAboutMe && !!manager && !recipients.find(({ user }) => user.id === manager.id);

		const sharableWithDirectReport =
			!sharedWithMe &&
			/* It's not about me */
			context0 &&
			context0.__typename === "User" &&
			context0.id !== user.id &&
			/* It's about a Direct Report */
			!!directReports &&
			directReports.edges.some(edge => edge?.node && edge.node.id == context0.id) &&
			/* Direct Report has not received it */
			!recipients.find(({ user }) => user.id === context0.id);

		if (user.id === creator.id) return this.renderCreatorActions(sharableWithManager, sharableWithDirectReport);
		if (sharedWithMe) return this.renderSharedWithMe();

		return null;
	}
}

export default withRouter(smart(MoreActions));
